import { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Collapse from "react-bootstrap/Collapse";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import httpService from "../../services/httpService";
import { FaBullseye, FaPlus, FaEdit, FaTrash, FaLock } from "react-icons/fa";
import '../../styles/PracticeModal.scss';

const CLUB_TILE = {
    border: '1.5px solid #dee2e6',
    borderRadius: '12px',
    padding: '18px 16px',
    background: '#fff',
    width: '100%',
};

const CUSTOM_TILE = {
    border: '1.5px solid #dee2e6',
    borderLeft: '3px solid #6C5CE7',
    borderRadius: '12px',
    padding: '14px 16px',
    background: 'rgba(108,92,231,0.04)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
};

const ListenerSkills = ({ clubGuid }) => {
    const [loading, setLoading]               = useState(true);
    const [clubSkills, setClubSkills]         = useState([]);
    const [customSkills, setCustomSkills]     = useState([]);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createTitle, setCreateTitle]       = useState('');
    const [createDesc, setCreateDesc]         = useState('');
    const [creating, setCreating]             = useState(false);

    const [editingGuid, setEditingGuid]       = useState(null);
    const [editTitle, setEditTitle]           = useState('');
    const [editDesc, setEditDesc]             = useState('');
    const [saving, setSaving]                 = useState(false);

    const [deletingGuid, setDeletingGuid]     = useState(null);

    useEffect(() => {
        if (!clubGuid) return;
        httpService.get(`/dash/club/${clubGuid}/skills`)
            .then((res) => {
                const all = res.data.skills || [];
                setClubSkills(all.filter((s) => !s.is_custom));
                setCustomSkills(all.filter((s) => s.is_custom));
            })
            .catch((err) => console.error('Error fetching skills:', err))
            .finally(() => setLoading(false));
    }, [clubGuid]);

    const handleCreate = () => {
        if (!createTitle.trim()) return;
        setCreating(true);
        httpService.post(`/dash/club/${clubGuid}/listener-skills`, {
            title: createTitle.trim(),
            description: createDesc.trim() || undefined,
        })
            .then((res) => {
                setCustomSkills((prev) => [...prev, res.data.skill]);
                setCreateTitle('');
                setCreateDesc('');
                setShowCreateForm(false);
            })
            .catch((err) => console.error('Error creating skill:', err))
            .finally(() => setCreating(false));
    };

    const startEdit = (skill) => {
        setEditingGuid(skill.guid);
        setEditTitle(skill.title);
        setEditDesc(skill.description || '');
    };

    const handleSaveEdit = (guid) => {
        if (!editTitle.trim()) return;
        setSaving(true);
        httpService.put(`/dash/club/${clubGuid}/listener-skills/${guid}`, {
            title: editTitle.trim(),
            description: editDesc.trim() || undefined,
        })
            .then((res) => {
                setCustomSkills((prev) =>
                    prev.map((s) => s.guid === guid ? res.data.skill : s)
                );
                setEditingGuid(null);
            })
            .catch((err) => console.error('Error updating skill:', err))
            .finally(() => setSaving(false));
    };

    const handleDelete = (guid) => {
        setDeletingGuid(guid);
        httpService.delete(`/dash/club/${clubGuid}/listener-skills/${guid}`)
            .then(() => setCustomSkills((prev) => prev.filter((s) => s.guid !== guid)))
            .catch((err) => console.error('Error deleting skill:', err))
            .finally(() => setDeletingGuid(null));
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    return (
        <div className="py-2">

            {/* ── Club Skills ─────────────────────────── */}
            <div className="mb-2" style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Club Skills
            </div>

            {clubSkills.length === 0 ? (
                <div className="text-center py-4 text-muted mb-4">
                    <FaBullseye style={{ fontSize: '2rem', opacity: 0.18 }} />
                    <p className="mt-2 mb-0" style={{ fontSize: '0.82rem' }}>No skills defined for this club yet.</p>
                </div>
            ) : (
                <Row className="g-3 mb-4">
                    {clubSkills.map((skill) => (
                        <Col key={skill.guid} xs={12} sm={6} lg={4} className="d-flex">
                            <div style={CLUB_TILE}>
                                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a', marginBottom: '6px' }}>
                                    {skill.title}
                                </div>
                                {skill.description && (
                                    <div style={{ fontSize: '0.76rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.5 }}>
                                        {skill.description}
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            {/* ── Your Skills ─────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-1">
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Your Skills
                </div>
                <Button size="sm" variant="outline-secondary" onClick={() => setShowCreateForm((v) => !v)}>
                    <FaPlus className="me-1" />Add Skill
                </Button>
            </div>

            <div className="skill-custom-info mb-3">
                <FaLock style={{ fontSize: '0.6rem' }} />
                Custom skills are private to you. They can be attached to your practices but are not visible to other listeners.
            </div>

            <Collapse in={showCreateForm}>
                <div>
                    <div className="skill-add-form mb-3">
                        <Form.Group className="mb-2">
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Skill title (required)"
                                value={createTitle}
                                onChange={(e) => setCreateTitle(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Control
                                size="sm"
                                as="textarea"
                                rows={2}
                                placeholder="Description (optional)"
                                value={createDesc}
                                onChange={(e) => setCreateDesc(e.target.value)}
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={handleCreate}
                                disabled={creating || !createTitle.trim()}
                            >
                                {creating && <Spinner animation="border" size="sm" style={{ width: '0.7rem', height: '0.7rem' }} className="me-1" />}
                                Save Skill
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShowCreateForm(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </Collapse>

            {customSkills.length === 0 ? (
                <p className="text-muted" style={{ fontSize: '0.82rem' }}>You haven't added any custom skills yet.</p>
            ) : (
                <Row className="g-3">
                    {customSkills.map((skill) => (
                        <Col key={skill.guid} xs={12} sm={6} lg={4} className="d-flex">
                            <div style={CUSTOM_TILE}>
                                <div style={{ fontSize: '0.56rem', fontWeight: 700, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Your skill
                                </div>
                                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    {skill.title}
                                </div>
                                {skill.description && editingGuid !== skill.guid && (
                                    <div style={{ fontSize: '0.76rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.5 }}>
                                        {skill.description}
                                    </div>
                                )}

                                {/* Inline edit form */}
                                {editingGuid === skill.guid && (
                                    <div className="mt-2">
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            className="mb-2"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                        />
                                        <Form.Control
                                            size="sm"
                                            as="textarea"
                                            rows={2}
                                            className="mb-2"
                                            placeholder="Description (optional)"
                                            value={editDesc}
                                            onChange={(e) => setEditDesc(e.target.value)}
                                        />
                                        <div className="d-flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={() => handleSaveEdit(skill.guid)}
                                                disabled={saving || !editTitle.trim()}
                                            >
                                                {saving && <Spinner animation="border" size="sm" style={{ width: '0.6rem', height: '0.6rem' }} className="me-1" />}
                                                Save
                                            </Button>
                                            <Button size="sm" variant="outline-secondary" onClick={() => setEditingGuid(null)}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Action buttons */}
                                {editingGuid !== skill.guid && (
                                    <div className="d-flex gap-1 mt-auto pt-2">
                                        <Button size="sm" variant="outline-secondary" onClick={() => startEdit(skill)}>
                                            <FaEdit />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => handleDelete(skill.guid)}
                                            disabled={deletingGuid === skill.guid}
                                        >
                                            {deletingGuid === skill.guid
                                                ? <Spinner animation="border" size="sm" style={{ width: '0.6rem', height: '0.6rem' }} />
                                                : <FaTrash />
                                            }
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

        </div>
    );
};

export default ListenerSkills;
